/**
 * GlobalView — EntityBottomSheet
 *
 * On mobile (≤ 768px), wraps entity popup content in a bottom sheet
 * with expand/minimize/close states.
 * On desktop, renders children as-is (no wrapper).
 *
 * States:
 *   expanded  → full details visible, scrollable
 *   minimized → compact pill bar (entity name), selection stays active
 *   closed    → deselected, nothing rendered
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import styles from './EntityBottomSheet.module.css';

interface EntityBottomSheetProps {
  /** Whether the sheet should be shown */
  visible: boolean;
  /** Entity display name (shown in minimized pill) */
  entityName: string;
  /** Optional subtitle (e.g. route, type) */
  entitySub?: string;
  /** Emoji/icon for minimized pill */
  icon?: string;
  /** Called when user closes the sheet (deselect) */
  onClose: () => void;
  /** Popup content to render inside the sheet */
  children: React.ReactNode;
  /** Desktop popup (rendered as-is on desktop) */
  desktopPopup: React.ReactNode;
}

type SheetState = 'expanded' | 'minimized' | 'hidden';

export default function EntityBottomSheet({
  visible,
  entityName,
  entitySub,
  icon = '📍',
  onClose,
  children,
  desktopPopup,
}: EntityBottomSheetProps) {
  const isMobile = useIsMobile();
  const [sheetState, setSheetState] = useState<SheetState>('hidden');
  const touchStartY = useRef(0);
  const prevEntityName = useRef('');

  // When a new entity is selected, expand
  useEffect(() => {
    if (visible && entityName !== prevEntityName.current) {
      setSheetState('expanded');
      prevEntityName.current = entityName;
    } else if (!visible) {
      setSheetState('hidden');
      prevEntityName.current = '';
    }
  }, [visible, entityName]);

  const handleMinimize = useCallback(() => {
    setSheetState('minimized');
  }, []);

  const handleExpand = useCallback(() => {
    setSheetState('expanded');
  }, []);

  const handleClose = useCallback(() => {
    setSheetState('hidden');
    onClose();
  }, [onClose]);

  // Swipe down to minimize
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    if (deltaY > 50) {
      // Swipe down
      if (sheetState === 'expanded') {
        setSheetState('minimized');
      } else if (sheetState === 'minimized') {
        // Don't close on swipe — force explicit close
      }
    } else if (deltaY < -50 && sheetState === 'minimized') {
      // Swipe up → expand
      setSheetState('expanded');
    }
  }, [sheetState]);

  // Desktop: render the popup as-is
  if (!isMobile) {
    return <>{desktopPopup}</>;
  }

  // Mobile: not visible → nothing
  if (!visible) return null;

  const sheetClass = [
    styles.sheet,
    sheetState === 'expanded' ? styles.sheetExpanded : '',
    sheetState === 'minimized' ? styles.sheetMinimized : '',
    sheetState === 'hidden' ? styles.sheetHidden : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      {/* Backdrop — prevents interaction with map while expanded */}
      {sheetState === 'expanded' && (
        <div className={styles.backdrop} onClick={handleMinimize} />
      )}

      <div
        className={sheetClass}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag handle bar */}
        <div className={styles.handle}>
          <div className={styles.handleBar} />
        </div>

        {/* Minimized pill bar (always rendered, visible via CSS transform) */}
        <div className={styles.pill} onClick={handleExpand}>
          <div className={styles.pillInfo}>
            <span className={styles.pillIcon}>{icon}</span>
            <span className={styles.pillName}>{entityName}</span>
            {entitySub && <span className={styles.pillSub}>{entitySub}</span>}
          </div>
          <div className={styles.pillActions}>
            <button
              className={styles.expandBtn}
              onClick={(e) => { e.stopPropagation(); handleExpand(); }}
              title="Expand"
            >
              ▲
            </button>
            <button
              className={styles.closeBtn}
              onClick={(e) => { e.stopPropagation(); handleClose(); }}
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Expanded content */}
        {sheetState === 'expanded' && (
          <>
            <div className={styles.header}>
              <div className={styles.headerLeft}>
                <span className={styles.entityName}>{icon} {entityName}</span>
                {entitySub && <span className={styles.entitySub}>{entitySub}</span>}
              </div>
              <div className={styles.headerActions}>
                <button
                  className={styles.minimizeBtn}
                  onClick={handleMinimize}
                  title="Minimize"
                  id="bottom-sheet-minimize"
                >
                  ▼
                </button>
                <button
                  className={styles.closeBtnHeader}
                  onClick={handleClose}
                  title="Close"
                  id="bottom-sheet-close"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className={styles.content}>
              {children}
            </div>
          </>
        )}
      </div>
    </>
  );
}
